import { AlgorithmTraceCallback, Chromosome, GenerationProgressCallback } from '../types/algorithm';
import { calculateChromosomeFitness } from '../utils/fitnessCalculator';
import {
  initializePopulation,
  tournamentSelection,
  singlePointCrossover,
  mutate,
  findBestChromosome,
} from './geneticAlgorithm';
import { SeededRNG, createSeededRNG } from '../utils/seededRNG';
import { incrementLocalSearchCalls } from '../utils/benchmarkInstrumentation';

/**
 * Standard SFLA + GA baseline hybrid.
 *
 * This baseline keeps the core SFLA memeplex search and augments it with
 * ordinary GA crossover and mutation inside each memeplex. It is intentionally
 * conservative so it can serve as the scientifically clean comparator for the
 * proposed SFLAGA variant.
 */

function createMemeplexes(
  population: Chromosome[],
  memeplexCount: number
): Chromosome[][] {
  const memeplexes: Chromosome[][] = Array(memeplexCount)
    .fill(null)
    .map(() => []);

  for (let i = 0; i < population.length; i++) {
    memeplexes[i % memeplexCount].push(population[i]);
  }

  return memeplexes;
}

function shuffleMemeplexes(
  memeplexes: Chromosome[][],
  population: Chromosome[]
): void {
  const memeplexCount = memeplexes.length;

  for (let i = 0; i < population.length; i++) {
    const memeplexIdx = i % memeplexCount;
    const frogIdx = Math.floor(i / memeplexCount);

    if (frogIdx < memeplexes[memeplexIdx].length) {
      population[i] = memeplexes[memeplexIdx][frogIdx];
    }
  }
}

function createRandomChromosome(
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  rng: SeededRNG
): Chromosome {
  const chromosome: Chromosome = {
    x: Array(taskCount)
      .fill(0)
      .map(() => rng.randomInt(serviceCount)),
    fitness: 0,
    rwp: 0,
  };

  calculateChromosomeFitness(chromosome, matrix, taskCount, serviceCount);
  return chromosome;
}

function updateWorstFrog(
  worst: Chromosome,
  guide: Chromosome,
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  rng: SeededRNG
): Chromosome {
  incrementLocalSearchCalls();
  const differingIndices: number[] = [];
  for (let i = 0; i < taskCount; i++) {
    if (worst.x[i] !== guide.x[i]) differingIndices.push(i);
  }

  const maxStep = Math.max(1, differingIndices.length);
  const candidate: Chromosome = {
    x: [...worst.x],
    fitness: 0,
    rwp: 0,
  };

  const step = rng.randomIntRange(1, maxStep);
  rng.shuffle(differingIndices);
  const toCopy = differingIndices.slice(0, step);
  for (const taskIdx of toCopy) {
    candidate.x[taskIdx] = guide.x[taskIdx];
  }

  calculateChromosomeFitness(candidate, matrix, taskCount, serviceCount);
  return candidate;
}

function selectParent(
  memeplex: Chromosome[],
  selectionRate: number,
  rng: SeededRNG
): Chromosome {
  if (rng.randomChance(selectionRate / 100)) {
    return memeplex[tournamentSelection(memeplex, rng)];
  }

  return memeplex[rng.randomInt(memeplex.length)];
}

function evolveMemeplex(
  memeplex: Chromosome[],
  globalBest: Chromosome,
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  selectionRate: number,
  crossoverRate: number,
  mutationRate: number,
  rng: SeededRNG,
  localIterations: number,
  generation: number = 0,
  memeplexIndex: number = 0,
  trace?: AlgorithmTraceCallback
): void {
  if (memeplex.length === 0) return;

  for (let iter = 0; iter < localIterations; iter++) {
    memeplex.sort((a, b) => b.fitness - a.fitness);
    const localBest = memeplex[0];
    const worstIdx = memeplex.length - 1;
    const worst = memeplex[worstIdx];

    let improved = updateWorstFrog(
      worst,
      localBest,
      taskCount,
      serviceCount,
      matrix,
      rng
    );
    trace?.({
      generation,
      phase: 'local_search',
      action: 'try_memeplex_best',
      detail: 'Attempted to improve the worst frog using the memeplex best frog.',
      entityIndex: memeplexIndex,
      previousFitness: worst.fitness,
      fitness: improved.fitness,
      accepted: improved.fitness > worst.fitness,
      metadata: { localIteration: iter + 1 },
    });

    if (improved.fitness > worst.fitness) {
      memeplex[worstIdx] = improved;
      continue;
    }

    improved = updateWorstFrog(
      worst,
      globalBest,
      taskCount,
      serviceCount,
      matrix,
      rng
    );
    trace?.({
      generation,
      phase: 'local_search',
      action: 'try_global_best',
      detail: 'Attempted to improve the worst frog using the global best frog.',
      entityIndex: memeplexIndex,
      previousFitness: worst.fitness,
      fitness: improved.fitness,
      accepted: improved.fitness > worst.fitness,
      metadata: { localIteration: iter + 1 },
    });

    if (improved.fitness > worst.fitness) {
      memeplex[worstIdx] = improved;
    } else {
      const randomChromosome = createRandomChromosome(taskCount, serviceCount, matrix, rng);
      memeplex[worstIdx] = randomChromosome;
      trace?.({
        generation,
        phase: 'replacement',
        action: 'random_frog_replacement',
        detail: 'Replaced the worst frog with a random chromosome after local search failed.',
        entityIndex: memeplexIndex,
        previousFitness: worst.fitness,
        fitness: randomChromosome.fitness,
        accepted: true,
        metadata: { localIteration: iter + 1 },
      });
    }
  }

  memeplex.sort((a, b) => b.fitness - a.fitness);
  const eliteCount = Math.max(1, Math.floor(memeplex.length * 0.1));

  const children: Chromosome[] = [];
  const numPairs = Math.max(1, Math.floor((memeplex.length - eliteCount) / 2));

  for (let i = 0; i < numPairs; i++) {
    if (!rng.randomChance(crossoverRate / 100)) {
      continue;
    }

    const parent1 = selectParent(memeplex, selectionRate, rng);
    let parent2 = selectParent(memeplex, selectionRate, rng);

    let retries = 0;
    while (parent2 === parent1 && memeplex.length > 1 && retries < 10) {
      parent2 = selectParent(memeplex, selectionRate, rng);
      retries++;
    }
    trace?.({
      generation,
      phase: 'selection',
      action: 'select_memeplex_parents',
      detail: 'Selected parent frogs for the GA phase inside the memeplex.',
      entityIndex: memeplexIndex,
      fitness: Math.max(parent1.fitness, parent2.fitness),
      metadata: { parent1Fitness: parent1.fitness, parent2Fitness: parent2.fitness },
    });

    let [child1, child2] = singlePointCrossover(parent1, parent2, taskCount, rng);
    trace?.({
      generation,
      phase: 'crossover',
      action: 'single_point_crossover',
      detail: 'Created two child frogs using GA crossover inside the memeplex.',
      entityIndex: memeplexIndex,
    });

    if (rng.randomChance(mutationRate / 100)) {
      child1 = mutate(child1, taskCount, serviceCount, rng);
      trace?.({
        generation,
        phase: 'mutation',
        action: 'mutate_child',
        detail: 'Applied random-gene mutation to child 1.',
        entityIndex: memeplexIndex,
      });
    }
    if (rng.randomChance(mutationRate / 100)) {
      child2 = mutate(child2, taskCount, serviceCount, rng);
      trace?.({
        generation,
        phase: 'mutation',
        action: 'mutate_child',
        detail: 'Applied random-gene mutation to child 2.',
        entityIndex: memeplexIndex,
      });
    }

    calculateChromosomeFitness(child1, matrix, taskCount, serviceCount);
    calculateChromosomeFitness(child2, matrix, taskCount, serviceCount);
    trace?.({
      generation,
      phase: 'evaluation',
      action: 'evaluate_children',
      detail: 'Evaluated SFLA-GA child frogs.',
      entityIndex: memeplexIndex,
      fitness: Math.max(child1.fitness, child2.fitness),
      metadata: { child1Fitness: child1.fitness, child2Fitness: child2.fitness },
    });
    children.push(child1, child2);
  }

  children.sort((a, b) => b.fitness - a.fitness);

  let replaceIdx = memeplex.length - 1;
  for (const child of children) {
    while (replaceIdx >= eliteCount) {
      if (child.fitness > memeplex[replaceIdx].fitness) {
        trace?.({
          generation,
          phase: 'replacement',
          action: 'replace_memeplex_member',
          detail: 'Replaced a non-elite memeplex frog with a fitter child.',
          entityIndex: memeplexIndex,
          previousFitness: memeplex[replaceIdx].fitness,
          fitness: child.fitness,
          accepted: true,
          solution: child.x,
        });
        memeplex[replaceIdx] = child;
        replaceIdx--;
        break;
      }
      replaceIdx--;
    }

    if (replaceIdx < eliteCount) {
      break;
    }
  }

  memeplex.sort((a, b) => b.fitness - a.fitness);
}

export async function runSFLAGABaseline(
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  parameters: {
    populationSize: number;
    generations: number;
    selectionRate: number;
    memeplexCount: number;
    crossoverRate: number;
    mutationRate: number;
    localIterations?: number;
  },
  onGenerationComplete?: GenerationProgressCallback,
  seed?: number,
  runtimeOptions?: {
    yieldForProgress?: boolean;
    reportEveryGeneration?: boolean;
    trace?: AlgorithmTraceCallback;
  }
): Promise<Chromosome> {
  const rng = createSeededRNG(seed);
  const shouldYieldForProgress = runtimeOptions?.yieldForProgress ?? true;
  const shouldReportEveryGeneration = runtimeOptions?.reportEveryGeneration ?? false;
  const trace = runtimeOptions?.trace;

  const population = initializePopulation(
    parameters.populationSize,
    taskCount,
    serviceCount,
    rng
  );
  trace?.({
    generation: 0,
    phase: 'initialization',
    action: 'initialize_population',
    detail: `Created ${parameters.populationSize} frogs.`,
    metadata: { populationSize: parameters.populationSize, taskCount, serviceCount },
  });

  for (const chrom of population) {
    calculateChromosomeFitness(chrom, matrix, taskCount, serviceCount);
  }
  let initialBest = findBestChromosome(population);
  trace?.({
    generation: 0,
    phase: 'evaluation',
    action: 'evaluate_initial_population',
    detail: 'Evaluated all frogs in the initial population.',
    bestFitness: initialBest.fitness,
    solution: initialBest.x,
  });

  population.sort((a, b) => b.fitness - a.fitness);
  let memeplexes = createMemeplexes(population, parameters.memeplexCount);
  trace?.({
    generation: 0,
    phase: 'memeplex',
    action: 'create_memeplexes',
    detail: `Created ${parameters.memeplexCount} memeplexes from the sorted frog population.`,
    metadata: { memeplexCount: parameters.memeplexCount },
  });

  let lastUpdateTime = Date.now();
  const UPDATE_INTERVAL = 100;
  const localIterations = Math.max(1, parameters.localIterations ?? 3);

  for (let gen = 0; gen < parameters.generations; gen++) {
    const globalBest = findBestChromosome(population);

    for (let memeplexIndex = 0; memeplexIndex < memeplexes.length; memeplexIndex++) {
      const memeplex = memeplexes[memeplexIndex];
      evolveMemeplex(
        memeplex,
        globalBest,
        taskCount,
        serviceCount,
        matrix,
        parameters.selectionRate,
        parameters.crossoverRate,
        parameters.mutationRate,
        rng,
        localIterations,
        gen + 1,
        memeplexIndex,
        trace
      );
    }

    shuffleMemeplexes(memeplexes, population);
    trace?.({
      generation: gen + 1,
      phase: 'memeplex',
      action: 'shuffle_memeplexes',
      detail: 'Interleaved memeplexes back into the population.',
    });
    population.sort((a, b) => b.fitness - a.fitness);
    memeplexes = createMemeplexes(population, parameters.memeplexCount);
    trace?.({
      generation: gen + 1,
      phase: 'memeplex',
      action: 'recreate_memeplexes',
      detail: 'Recreated memeplexes for the next generation.',
      bestFitness: population[0]?.fitness,
      solution: population[0]?.x,
    });

    const now = Date.now();
    const shouldUpdate =
      shouldReportEveryGeneration ||
      now - lastUpdateTime > UPDATE_INTERVAL ||
      gen % 10 === 9 ||
      gen === parameters.generations - 1;

    if (shouldUpdate) {
      if (onGenerationComplete) {
        onGenerationComplete(gen + 1, population[0].fitness, population[0].x);
      }
      trace?.({
        generation: gen + 1,
        phase: 'generation',
        action: 'generation_complete',
        detail: 'Generation best solution recorded.',
        bestFitness: population[0].fitness,
        solution: population[0].x,
      });
      lastUpdateTime = now;
      if (shouldYieldForProgress) {
        await new Promise((resolve) => setTimeout(resolve, 0));
      }
    }
  }

  return findBestChromosome(population);
}
